import { create } from 'zustand';

// Base z-index starts above the map canvas and overlays
const BASE_Z_INDEX = 1005;

interface ModalZIndexState {
  // Map of modalId -> current zIndex
  zIndices: Record<string, number>;
  // Highest z-index counter
  topZIndex: number;
  // Currently active/front-most modal ID
  activeModalId: string | null;
  // Bring modal with modalId to the top
  bringToFront: (modalId: string) => void;
  // Check if modalId is currently the top/active modal
  isTopModal: (modalId: string) => boolean;
  // Reset
  reset: () => void;
}

export const useModalZIndexStore = create<ModalZIndexState>((set, get) => ({
  zIndices: {},
  topZIndex: BASE_Z_INDEX,
  activeModalId: null,
  bringToFront: (modalId: string) => {
    if (!modalId) return;
    const { topZIndex, zIndices, activeModalId } = get();
    // If this modal is already at the very top, ensure activeModalId is set and return
    if (zIndices[modalId] === topZIndex && topZIndex > BASE_Z_INDEX) {
      if (activeModalId !== modalId) {
        set({ activeModalId: modalId });
      }
      return;
    }
    const nextZIndex = Math.max(topZIndex + 1, BASE_Z_INDEX + 1);
    set({
      topZIndex: nextZIndex,
      activeModalId: modalId,
      zIndices: {
        ...zIndices,
        [modalId]: nextZIndex,
      },
    });
  },
  isTopModal: (modalId: string) => {
    const { activeModalId, zIndices, topZIndex } = get();
    if (activeModalId === modalId) return true;
    return (zIndices[modalId] || 0) === topZIndex && topZIndex > BASE_Z_INDEX;
  },
  reset: () => {
    set({ zIndices: {}, topZIndex: BASE_Z_INDEX, activeModalId: null });
  },
}));

export default useModalZIndexStore;
